import {
	wrapper,
	createApiClient,
	Result,
	ApiError,
	PaginationParams,
} from "../base";
import UserDo = App.Data.UserDO;
import LogDO = App.Data.LogDO;
import { LaravelPaginatedCollection } from "@/types/collections";
import UserDO = App.Data.UserDO;
import UpdatePermissionDTO = App.Data.UpdatePermissionDTO;
import UserPhoneVerificationPostData = App.Data.UserPhoneVerificationPostData;
import PublicUserDO = App.Data.PublicUserDO;
import UserNotificationStateDO = App.Data.UserNotificationStateDO;
import UserListViewDTO = App.Data.UserListViewDTO;

const basePath = `user`;
const baseClient = createApiClient();

type UserParams = PaginationParams & {
	sort?: string;
	"filter[search]"?: string;
};

export type passwordFieldsType = {
	current_password: string;
	password: string;
	password_confirmation: string;
};

export type passwordResetFieldsType = {
	email: string;
	token: string;
	password: string;
	password_confirmation: string;
};

export default class {
	authenticate(body: {
		email: string;
		password: string;
	}): Promise<Result<any, ApiError>> {
		return wrapper(baseClient.post(`authenticate`, body));
	}

	logout(): Promise<Result<any, ApiError>> {
		return wrapper(baseClient.post(`logout`));
	}

	getCurrentUser(): Promise<Result<UserDo, ApiError>> {
		return wrapper(baseClient.get(`${basePath}/getCurrent`));
	}

	updateLastLogin(): Promise<Result<UserDo, ApiError>> {
		return wrapper(baseClient.put(`${basePath}/update-last-login`));
	}

	getEnvironmentUsers(): Promise<Result<PublicUserDO[], ApiError>> {
		return wrapper(baseClient.get(`${basePath}/environment-users`));
	}

	getCurrentUserPermissions(): Promise<Result<[], ApiError>> {
		return wrapper(baseClient.get(`${basePath}/permissions`));
	}

	getDefinedCompanies(): Promise<Result<[], ApiError>> {
		return wrapper(baseClient.get(`${basePath}/getDefinedCompanies`));
	}

	getCurrentUserLogs(): Promise<Result<LogDO[], ApiError>> {
		return wrapper(baseClient.get(`${basePath}/logs`));
	}

	getCurrentUserFavoriteDimensions(): Promise<
		Result<App.Data.FavoriteDimensionDO[], ApiError>
	> {
		return wrapper(baseClient.get(`${basePath}/get-favorite-dimensions`));
	}

	toggleCurrentUserFavoriteDimension(
		dimension: App.Data.DimensionDO
	): Promise<Result<[], ApiError>> {
		return wrapper(
			baseClient.put(
				`${basePath}/toggle-favorite-property/${dimension.name_clean}`
			)
		);
	}

	unpinFavoriteDimension(
		dimension: App.Data.DimensionDO
	): Promise<Result<[], ApiError>> {
		return wrapper(
			baseClient.put(
				`${basePath}/unpin-favorite-dimension/${dimension.name_clean}`
			)
		);
	}

	getNewUser(): Promise<Result<UserDo, ApiError>> {
		return wrapper(baseClient.get(`${basePath}/new`));
	}

	getUserListViews(
		params: UserParams = {}
	): Promise<Result<LaravelPaginatedCollection<UserListViewDTO>, ApiError>> {
		return wrapper(baseClient.get(`${basePath}`, { params }));
	}

	getUserByUUID(uuid: string | string[]): Promise<Result<UserDo, ApiError>> {
		return wrapper(baseClient.get(`${basePath}/${uuid}`));
	}

	createUser(user: UserDo): Promise<Result<UserDo, ApiError>> {
		return wrapper(baseClient.post(`${basePath}`, user));
	}

	removeUser(uuid: string): Promise<Result<string, ApiError>> {
		return wrapper(baseClient.delete(`${basePath}/${uuid}`));
	}

	updatePermission(
		updatePermissionDTO: UpdatePermissionDTO,
		uuid: string | string[]
	): Promise<Result<string, ApiError>> {
		return wrapper(
			baseClient.put(
				`${basePath}/${uuid}/updatePermission/${updatePermissionDTO.name}`,
				updatePermissionDTO
			)
		);
	}

	updateUser(user: UserDO): Promise<Result<UserDO, ApiError>> {
		return wrapper(baseClient.put(`${basePath}/${user.uuid}`, user));
	}

	createPublicApiToken(
		uuid: string,
		name: string
	): Promise<Result<String, ApiError>> {
		return wrapper(
			baseClient.post(`${basePath}/${uuid}/create-public-api-token`, { name })
		);
	}

	revokePublicApiToken(
		uuid: string,
		token_id: number
	): Promise<Result<UserDo, ApiError>> {
		return wrapper(
			baseClient.put(`${basePath}/${uuid}/revoke-public-api-token`, {
				token_id,
			})
		);
	}

	renewPublicApiToken(
		uuid: string,
		token_id: number
	): Promise<Result<UserDo, ApiError>> {
		return wrapper(
			baseClient.put(`${basePath}/${uuid}/renew-public-api-token`, {
				token_id,
			})
		);
	}

	removePublicApiToken(
		uuid: string,
		token_id: number
	): Promise<Result<UserDo, ApiError>> {
		return wrapper(
			baseClient.delete(
				`${basePath}/${uuid}/delete-public-api-token/${token_id}`
			)
		);
	}

	getAllPublicApiTokens(
		uuid: string
	): Promise<Result<App.Data.PersonalAccessTokenDO[], ApiError>> {
		return wrapper(baseClient.get(`${basePath}/${uuid}/public-api-tokens`));
	}

	updateNotificationTypeChannel(
		uuid: string | string[],
		updateNotification: App.Data.UpdateNotificationDTO
	): Promise<Result<[], ApiError>> {
		return wrapper(
			baseClient.put(
				`${basePath}/${uuid}/update-notification-type-channel`,
				updateNotification
			)
		);
	}

	impersonateUser(uuid: string | string[]): Promise<Result<String, ApiError>> {
		return wrapper(baseClient.get(`${basePath}/impersonate/${uuid}`));
	}

	stopImpersonate(uuid: string | string[]): Promise<Result<String, ApiError>> {
		return wrapper(baseClient.get(`${basePath}/stop-impersonate/${uuid}`));
	}

	updateProfile(user: UserDO): Promise<Result<UserDO, ApiError>> {
		return wrapper(baseClient.put(`${basePath}/profile`, user));
	}

	updateNotificationTypeChannelFromProfile(
		updateNotification: App.Data.UpdateNotificationDTO
	): Promise<Result<UserDO, ApiError>> {
		return wrapper(
			baseClient.put(
				`${basePath}/profile/update-notification-type-channel`,
				updateNotification
			)
		);
	}

	getAvailableNotificationEvents(
		uuid: string
	): Promise<Result<App.Data.NotificationEventDO[], ApiError>> {
		return wrapper(
			baseClient.get(`${basePath}/${uuid}/get-available-notification-events`)
		);
	}

	getAvailableNotificationEventsForProfile(): Promise<
		Result<App.Data.NotificationEventDO[], ApiError>
	> {
		return wrapper(
			baseClient.get(`${basePath}/profile/get-available-notification-events`)
		);
	}

	updateNotificationEventsFromProfile(
		notificationEvents: App.Data.NotificationEventDO[]
	): Promise<Result<UserDO, ApiError>> {
		return wrapper(
			baseClient.put(
				`${basePath}/profile/update-notification-events`,
				notificationEvents
			)
		);
	}

	updateNotificationEvents(
		uuid: string,
		notificationEvents: App.Data.NotificationEventDO[]
	): Promise<Result<UserDO, ApiError>> {
		return wrapper(
			baseClient.put(
				`${basePath}/${uuid}/update-notification-events`,
				notificationEvents
			)
		);
	}

	// Fortify routes
	confirmedPasswordStatus(): Promise<Result<any, ApiError>> {
		return wrapper(baseClient.get(`${basePath}/confirmed-password-status`));
	}

	confirmPassword(password: string): Promise<Result<any, ApiError>> {
		return wrapper(
			baseClient.post(`${basePath}/confirm-password`, { password })
		);
	}

	enableTwoFactorAuthentication(): Promise<Result<any, ApiError>> {
		return wrapper(baseClient.post(`${basePath}/two-factor-authentication`));
	}

	confirmedTwoFactorAuthentication(
		code: string
	): Promise<Result<any, ApiError>> {
		return wrapper(
			baseClient.post(`${basePath}/confirmed-two-factor-authentication`, {
				code: code,
			})
		);
	}

	validateTwoFactorChallenge(data: object): Promise<Result<any, ApiError>> {
		return wrapper(baseClient.post(`/two-factor-challenge`, data));
	}

	disableTwoFactorAuthentication(): Promise<Result<any, ApiError>> {
		return wrapper(baseClient.delete(`${basePath}/two-factor-authentication`));
	}

	getTwoFactorQRCode(): Promise<Result<any, ApiError>> {
		return wrapper(baseClient.get(`${basePath}/two-factor-qr-code`));
	}

	getTwoFactorSecretKey(): Promise<Result<any, ApiError>> {
		return wrapper(baseClient.get(`${basePath}/two-factor-secret-key`));
	}

	getTwoFactorRecoveryCodes(): Promise<Result<any, ApiError>> {
		return wrapper(baseClient.get(`${basePath}/two-factor-recovery-codes`));
	}

	StoreTwoFactorRecoveryCodes(): Promise<Result<any, ApiError>> {
		return wrapper(baseClient.post(`${basePath}/two-factor-recovery-codes`));
	}

	updateProfilePassword(
		passwordFields: passwordFieldsType
	): Promise<Result<any, ApiError>> {
		return wrapper(baseClient.put(`${basePath}/password`, passwordFields));
	}

	updateUserPassword(
		user: UserDO,
		passwordFields: passwordFieldsType
	): Promise<Result<UserDO, ApiError>> {
		return wrapper(
			baseClient.put(`${basePath}/${user.uuid}/password`, passwordFields)
		);
	}

	forgotPassword(email: string): Promise<Result<any, ApiError>> {
		return wrapper(baseClient.post(`/forgot-password`, { email }));
	}

	resetPassword(
		passwordFields: passwordResetFieldsType
	): Promise<Result<any, ApiError>> {
		return wrapper(baseClient.post(`/reset-password`, passwordFields));
	}

	verifyPhone(
		data: UserPhoneVerificationPostData
	): Promise<Result<any, ApiError>> {
		return wrapper(baseClient.post(`${basePath}/verify-phone`, data));
	}

	sendCodeForPhone(
		data: UserPhoneVerificationPostData
	): Promise<Result<any, ApiError>> {
		return wrapper(baseClient.post(`${basePath}/verify-phone-check`, data));
	}

	getUnreadNotificationCount(): Promise<
		Result<UserNotificationStateDO, ApiError>
	> {
		return wrapper(baseClient.get(`${basePath}/get-unread-notification-count`));
	}

	markNotificationAsRead(id: string): Promise<Result<any, ApiError>> {
		return wrapper(baseClient.post(`${basePath}/mark-as-read/${id}`));
	}
}
